/**
 * SCI Field QC App — photos on the phone.
 *
 * Three requirements from the spec, in the order they matter:
 *
 * 1. "Images are downscaled ON THE PHONE before upload (long edge 1600 px,
 *    JPEG q0.8), taking a 3 MB capture to roughly 400 KB." Site WiFi is the
 *    constraint; a full-resolution upload is the difference between a photo
 *    landing and a QC person giving up on photos.
 *
 * 2. "Photos upload directly to R2 via a short-lived signed URL, then the key
 *    is attached to the record" — three steps (grant, PUT, attach), each of
 *    which can fail independently on a bad-signal site.
 *
 * 3. "Photo upload failure: the record saves regardless; the photo is queued
 *    and retried, and shows as pending rather than silently vanishing."
 *
 * Point 3 is why this file is a queue and not a function. `capturePhoto()`
 * resolves as soon as the image is downscaled and safely on the device — it
 * never makes the caller wait for, or care about, the network. The record it
 * belongs to saves on its own path. The queue then works through the three
 * steps in the background, retrying with backoff, and every entry that has not
 * landed is visible in `pendingPhotos` until it does.
 *
 * "Safely on the device" means IndexedDB, not memory: a locked screen, a
 * reloaded tab or a dead battery must not lose a photo any more than it loses
 * a part-filled record. `restorePhotoQueue()` at boot picks up where the
 * previous session stopped. Where IndexedDB is unavailable (private mode,
 * quota, an old WebView) the queue degrades to memory-only rather than
 * refusing to take the photo at all.
 *
 * Resume is per-step: once the bytes are in R2 the key is remembered, so a
 * failure in the attach step never re-uploads the image. The attach route is
 * idempotent on that key, so a retry that duplicates an attach which actually
 * succeeded returns the existing photo rather than creating a second row.
 */

import { derived, get, writable, type Readable } from 'svelte/store';
import { api, isApiError, photoContentUrl } from './api';
import {
  PHOTO_JPEG_QUALITY,
  PHOTO_MAX_BYTES,
  PHOTO_MAX_EDGE_PX,
  type AttachPhotoRequest,
  type Photo,
  type PhotoSection,
  type PhotoUploadUrlResponse,
  type Timestamp
} from './types';

export { photoContentUrl };

/* ==========================================================================
 * Downscaling
 * ======================================================================== */

/** The JPEG the phone actually uploads. */
export interface DownscaledPhoto {
  blob: Blob;
  width: number;
  height: number;
  /** Bytes after downscaling — what actually crosses the network. */
  bytes: number;
  /** Bytes of the original capture, for the "3 MB -> 400 KB" reality check. */
  original_bytes: number;
  content_type: string;
}

export interface Dimensions {
  width: number;
  height: number;
}

/**
 * Fit (width x height) inside a square of `maxEdge`, preserving aspect ratio.
 *
 * Never upscales: a picture that is already small is re-encoded, not blown up.
 * Rounds to at least 1 px in each direction so a freak 1 x 4000 image cannot
 * produce a zero-width canvas.
 */
export function scaledDimensions(
  width: number,
  height: number,
  maxEdge: number = PHOTO_MAX_EDGE_PX
): Dimensions {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return { width: 1, height: 1 };
  }
  const longest = Math.max(width, height);
  const scale = longest > maxEdge ? maxEdge / longest : 1;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale))
  };
}

/** Decode a captured file, honouring EXIF orientation where the browser can. */
async function decode(file: Blob): Promise<ImageBitmap> {
  try {
    // Phone cameras record rotation in EXIF rather than rotating the pixels.
    // Without this a portrait photo of a batch computer lands sideways in the
    // QA record.
    return await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    return await createImageBitmap(file);
  }
}

/** Draw onto whichever canvas this browser has, and encode to JPEG. */
async function encodeJpeg(
  bitmap: ImageBitmap,
  size: Dimensions,
  quality: number
): Promise<Blob> {
  if (typeof OffscreenCanvas !== 'undefined') {
    const canvas = new OffscreenCanvas(size.width, size.height);
    const context = canvas.getContext('2d');
    if (context) {
      context.drawImage(bitmap, 0, 0, size.width, size.height);
      return await canvas.convertToBlob({ type: 'image/jpeg', quality });
    }
  }

  const canvas = document.createElement('canvas');
  canvas.width = size.width;
  canvas.height = size.height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('This device cannot process photos.');
  context.drawImage(bitmap, 0, 0, size.width, size.height);

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', quality)
  );
  if (!blob) throw new Error('This device cannot process photos.');
  return blob;
}

/**
 * Downscale a capture to the spec's limits before it ever touches the network.
 *
 * Always re-encodes as JPEG, even when the image is already small enough:
 * q0.8 is where the bulk of the saving comes from on a phone capture, and it
 * also normalises HEIC/PNG/WebP captures to the one type the API stores.
 */
export async function downscalePhoto(
  file: Blob,
  maxEdge: number = PHOTO_MAX_EDGE_PX,
  quality: number = PHOTO_JPEG_QUALITY
): Promise<DownscaledPhoto> {
  const bitmap = await decode(file);
  const size = scaledDimensions(bitmap.width, bitmap.height, maxEdge);
  try {
    const blob = await encodeJpeg(bitmap, size, quality);
    return {
      blob,
      width: size.width,
      height: size.height,
      bytes: blob.size,
      original_bytes: file.size,
      content_type: 'image/jpeg'
    };
  } finally {
    bitmap.close();
  }
}

/* ==========================================================================
 * Fingerprinting — duplicate photos
 *
 * "I tried to upload multiple of the same photo. I do not want duplicates
 * right now." Two layers, and this is the piece both are built on:
 *
 *  - the CLIENT courtesy (`guardAgainstDuplicatePhoto`, below) hashes the
 *    ORIGINAL file, before any downscaling, and refuses to queue a second
 *    copy of a file this device has already added to the pour;
 *  - the SERVER rule (`attempt()`, further down) hashes the DOWNSCALED bytes
 *    — the ones actually crossing the network — and sends that as
 *    `content_hash` so the R2 key itself is the dedupe key (src/api/photos.ts
 *    `buildPhotoKey`). The server is the rule; the client is the courtesy —
 *    this app has been bitten before by protections that existed only in the
 *    UI, so the client check never stands alone.
 *
 * Original bytes, not downscaled: `downscalePhoto` re-encodes through a
 * canvas, and nothing here needs to trust that two runs of that encoder
 * produce byte-identical output for the same input. Hashing the file exactly
 * as the OS handed it over is what makes "the same file picked twice" an
 * exact, stable comparison rather than one that merely usually works.
 * ======================================================================== */

/** Lowercase hex SHA-256 of `blob`'s bytes. */
export async function sha256Hex(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Thrown by `guardAgainstDuplicatePhoto` when the fingerprint has already
 * been added to this pour. Caught by PhotoField, which tells the person
 * rather than queueing a silent no-op — "the app noticed", not "the app
 * failed".
 */
export class DuplicatePhotoError extends Error {
  constructor(message = 'That photo has already been added.') {
    super(message);
    this.name = 'DuplicatePhotoError';
  }
}

/* ==========================================================================
 * Pending photos
 * ======================================================================== */

export type PendingPhotoStatus = 'queued' | 'uploading' | 'failed';

/**
 * A photo that has been taken but is not yet a row in the record.
 *
 * snake_case throughout, matching the wire shapes it is built from and turns
 * into, so a pending photo and an attached `Photo` read the same way in the UI.
 */
export interface PendingPhoto {
  /** Local id. Also the IndexedDB key. */
  id: string;
  pour_id: number;
  section: PhotoSection | null;
  row_id: number | null;
  caption: string | null;
  taken_at: Timestamp;
  width: number;
  height: number;
  bytes: number;
  /** Set once the bytes are in R2; a retry then resumes at the attach step. */
  r2_key: string | null;
  status: PendingPhotoStatus;
  attempts: number;
  /** Last failure, for the pending badge. null while it is still going well. */
  error: string | null;
  /** Epoch ms. The queue will not touch this entry before then. */
  next_attempt_at: number;
  /**
   * False when the server refused in a way retrying cannot fix (read-only
   * session, pour deleted, image rejected). The entry stays visible with its
   * reason so the user can retry by hand or discard it — it is never dropped
   * silently.
   */
  retryable: boolean;
  /** Object URL of the downscaled image, so the thumbnail appears at once. */
  preview_url: string;
  /**
   * SHA-256 of the ORIGINAL file this entry was captured from, or null when
   * hashing was unavailable. Recorded in the duplicate-fingerprint ledger at
   * enqueue time (see `guardAgainstDuplicatePhoto`); kept here too so a
   * caller with the entry in hand never needs a second lookup.
   */
  fingerprint: string | null;
}

/** Everything but the object URL, which cannot survive a reload. */
type PersistedPhoto = Omit<PendingPhoto, 'preview_url'>;

const queue = writable<PendingPhoto[]>([]);

/** Photos captured but not yet attached. Shown as pending, never dropped. */
export const pendingPhotos: Readable<PendingPhoto[]> = { subscribe: queue.subscribe };

/** True when anything is still waiting to upload. */
export const hasPendingPhotos: Readable<boolean> = derived(queue, (list) => list.length > 0);

/**
 * The pending photos belonging to one attachment point, so a PhotoField can
 * show its own queue and nobody else's.
 */
export function pendingPhotosFor(
  pourId: number,
  section: PhotoSection | null = null,
  rowId: number | null = null
): Readable<PendingPhoto[]> {
  return derived(queue, (list) =>
    list.filter(
      (item) =>
        item.pour_id === pourId &&
        (item.section ?? null) === section &&
        (item.row_id ?? null) === rowId
    )
  );
}

function patchEntry(id: string, change: Partial<PendingPhoto>): PendingPhoto | null {
  const list = get(queue);
  const index = list.findIndex((item) => item.id === id);
  if (index === -1) return null;

  const updated: PendingPhoto = { ...list[index]!, ...change };
  const next = list.slice();
  next[index] = updated;
  queue.set(next);

  // Persisted so the retry state survives a reload as well as the bytes do.
  void deps.store.patch(id, strip(updated));
  return updated;
}

function removeEntry(id: string, revoke: boolean): void {
  const entry = get(queue).find((item) => item.id === id);
  queue.update((list) => list.filter((item) => item.id !== id));
  if (entry && revoke && entry.preview_url) {
    try {
      URL.revokeObjectURL(entry.preview_url);
    } catch {
      /* nothing to revoke */
    }
  }
  void deps.store.delete(id);
}

function strip(entry: PendingPhoto): PersistedPhoto {
  const { preview_url: _preview, ...rest } = entry;
  return rest;
}

/* ==========================================================================
 * Device storage — IndexedDB, degrading to memory
 * ======================================================================== */

/** The bytes plus their metadata, so a reload can resume mid-queue. */
export interface QueuedRecord {
  id: string;
  blob: Blob;
  photo: PersistedPhoto;
}

export interface PhotoBlobStore {
  put(record: QueuedRecord): Promise<void>;
  patch(id: string, photo: PersistedPhoto): Promise<void>;
  get(id: string): Promise<QueuedRecord | null>;
  all(): Promise<QueuedRecord[]>;
  delete(id: string): Promise<void>;
}

const DB_NAME = 'sci-photo-queue';
// v2 adds FINGERPRINT_STORE below (duplicate-photo detection). The upgrade
// only ADDS a store — STORE_NAME and everything already in it are untouched,
// so a device with weeks of stranded photos in `queued` loses nothing.
const DB_VERSION = 2;
const STORE_NAME = 'queued';
const FINGERPRINT_STORE = 'fingerprints';

/**
 * A problem with the on-device photo database that is worth TELLING the
 * person about, as opposed to the ordinary IndexedDB failures this file
 * silently degrades from (quota, private mode, a browser that killed the
 * database — see `resilientStore`/`resilientFingerprintStore`'s own
 * comments). Both cases below are specific to the DB_VERSION 1 -> 2 bump
 * this fix-round introduced, reviewed as I1/I2:
 *
 *  - 'blocked': another tab is holding an older connection open across the
 *    version bump (`openDb()`'s `onblocked`). Capture and restore are never
 *    wedged by this — the open's promise is rejected, not left pending, so
 *    the existing memory fallback takes over immediately — but nothing else
 *    says so, and a stalled Add-photo button reproduces the owner's exact
 *    original complaint. The shell turns this into an instruction: close
 *    the app's other tabs, or fully close and reopen it.
 *  - 'version-mismatch': this build's database is a HIGHER version than the
 *    code now running expects (the shape a rollback produces once a phone
 *    has loaded a newer build). The bytes are still on disk; only this
 *    build cannot read them. Silence here reads as "my photos are gone",
 *    which is the one thing that could make someone clear site data and
 *    actually destroy them — so this is loud, not swallowed.
 */
export type PhotoStorageProblem = 'blocked' | 'version-mismatch' | null;

const storageProblem = writable<PhotoStorageProblem>(null);
export const photoStorageProblem: Readable<PhotoStorageProblem> = { subscribe: storageProblem.subscribe };

function memoryStore(): PhotoBlobStore {
  const map = new Map<string, QueuedRecord>();
  return {
    async put(record) {
      map.set(record.id, record);
    },
    async patch(id, photo) {
      const existing = map.get(id);
      if (existing) map.set(id, { ...existing, photo });
    },
    async get(id) {
      return map.get(id) ?? null;
    },
    async all() {
      return [...map.values()];
    },
    async delete(id) {
      map.delete(id);
    }
  };
}

/**
 * Open the queue's IndexedDB database — the ONE place that decides whether a
 * stale connection can wedge capture or restore. Reviewed as I1/I2:
 *
 * `onblocked` fires when another tab holds an older connection open across
 * this fix-round's DB_VERSION 1 -> 2 bump; without a handler the request
 * never settles, and — because `resilientStore`/`resilientFingerprintStore`
 * cannot fall back to memory until something actually THROWS — restore and
 * every new capture would hang on that stale tab's phone until it closes.
 * Rejecting here is what lets the existing fallback take over immediately.
 *
 * `onerror` with a `VersionError` name is the shape a ROLLBACK produces
 * once a phone has loaded this build: the on-disk database is now a higher
 * version than the code asking for it. That already rejected before this
 * fix-round (no hang), but silently — `resilientStore` swallows it into the
 * same quiet memory fallback as a quota error or private mode, and a crew
 * member just sees an empty queue with no explanation. Flagging it
 * specifically (not swallowing it) is what lets the shell say something
 * true and calming instead of nothing.
 *
 * `db.onversionchange` on a SUCCESSFUL open closes this connection the
 * moment some OTHER tab tries to open a later version — the fix for the
 * NEXT bump: a tab running this code will never be the stale connection
 * that blocks somebody else's upgrade.
 */
export function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = deps.openIndexedDb(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        database.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
      if (!database.objectStoreNames.contains(FINGERPRINT_STORE)) {
        database.createObjectStore(FINGERPRINT_STORE);
      }
    };
    request.onblocked = () => {
      storageProblem.set('blocked');
      reject(new Error('IndexedDB upgrade blocked by another open tab.'));
    };
    request.onsuccess = () => {
      const database = request.result;
      database.onversionchange = () => database.close();
      storageProblem.set(null);
      resolve(database);
    };
    request.onerror = () => {
      if (request.error?.name === 'VersionError') storageProblem.set('version-mismatch');
      reject(request.error ?? new Error('IndexedDB unavailable.'));
    };
  });
}

function idbStore(): PhotoBlobStore {
  let handle: Promise<IDBDatabase> | null = null;
  const database = () => (handle ??= openDb());

  async function run<T>(
    mode: IDBTransactionMode,
    action: (store: IDBObjectStore) => IDBRequest
  ): Promise<T> {
    const db = await database();
    return new Promise<T>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, mode);
      const request = action(tx.objectStore(STORE_NAME));
      request.onsuccess = () => resolve(request.result as T);
      request.onerror = () => reject(request.error ?? new Error('IndexedDB write failed.'));
    });
  }

  const put = async (record: QueuedRecord): Promise<void> => {
    await run<IDBValidKey>('readwrite', (store) => store.put(record));
  };
  const read = async (id: string): Promise<QueuedRecord | null> =>
    (await run<QueuedRecord | undefined>('readonly', (store) => store.get(id))) ?? null;

  return {
    put,
    get: read,
    async patch(id, photo) {
      const existing = await read(id);
      if (existing) await put({ ...existing, photo });
    },
    async all() {
      return (await run<QueuedRecord[]>('readonly', (store) => store.getAll())) ?? [];
    },
    async delete(id) {
      await run<undefined>('readwrite', (store) => store.delete(id));
    }
  };
}

/**
 * A store that swallows its own failures.
 *
 * Losing device persistence must never lose the photo: the in-memory queue is
 * still live, so the upload continues; only "survives a reload" is given up.
 */
function resilientStore(): PhotoBlobStore {
  const fallback = memoryStore();
  let primary: PhotoBlobStore | null = null;
  try {
    primary = typeof indexedDB === 'undefined' ? null : idbStore();
  } catch {
    primary = null;
  }

  async function tolerate<T>(
    action: (store: PhotoBlobStore) => Promise<T>,
    fallbackValue: T
  ): Promise<T> {
    if (primary) {
      try {
        return await action(primary);
      } catch {
        // One failure is enough: a store that has thrown once (quota, private
        // mode, a browser that killed the database) will keep throwing.
        primary = null;
      }
    }
    try {
      return await action(fallback);
    } catch {
      return fallbackValue;
    }
  }

  return {
    put: (record) => tolerate((s) => s.put(record), undefined),
    patch: (id, photo) => tolerate((s) => s.patch(id, photo), undefined),
    get: (id) => tolerate((s) => s.get(id), null),
    all: () => tolerate((s) => s.all(), []),
    delete: (id) => tolerate((s) => s.delete(id), undefined)
  };
}

/* ==========================================================================
 * Duplicate-fingerprint ledger
 *
 * A tiny companion to the blob store above: which (pour, hash) pairs this
 * device has already added, pending or attached. Written at enqueue time —
 * an attached photo's fingerprint has to outlive its pending entry (which IS
 * deleted, blob included, once uploaded) for "already attached" duplicates
 * to be caught at all — and REMOVED at discard time: discarding a photo
 * means "this is not in the record", and a photo that is not in the record
 * cannot be a duplicate of anything. Coordinator ruling, this fix-round: an
 * earlier version of this ledger was append-only forever, which turned
 * Discard from "remove this from the queue" into a silent, permanent
 * "never let me add this image again" — a different, unasked-for intent.
 * See `discardPhoto`'s own call to `fingerprints.remove`, below.
 * ======================================================================== */

export interface PhotoFingerprintStore {
  has(pourId: number, hash: string): Promise<boolean>;
  add(pourId: number, hash: string): Promise<void>;
  remove(pourId: number, hash: string): Promise<void>;
}

export function fingerprintKey(pourId: number, hash: string): string {
  return `${pourId}:${hash}`;
}

export function memoryFingerprintStore(): PhotoFingerprintStore {
  const seen = new Set<string>();
  return {
    async has(pourId, hash) {
      return seen.has(fingerprintKey(pourId, hash));
    },
    async add(pourId, hash) {
      seen.add(fingerprintKey(pourId, hash));
    },
    async remove(pourId, hash) {
      seen.delete(fingerprintKey(pourId, hash));
    }
  };
}

export function idbFingerprintStore(): PhotoFingerprintStore {
  let handle: Promise<IDBDatabase> | null = null;
  const database = () => (handle ??= openDb());

  async function run<T>(
    mode: IDBTransactionMode,
    action: (store: IDBObjectStore) => IDBRequest
  ): Promise<T> {
    const db = await database();
    return new Promise<T>((resolve, reject) => {
      const tx = db.transaction(FINGERPRINT_STORE, mode);
      const request = action(tx.objectStore(FINGERPRINT_STORE));
      request.onsuccess = () => resolve(request.result as T);
      request.onerror = () => reject(request.error ?? new Error('IndexedDB write failed.'));
    });
  }

  return {
    async has(pourId, hash) {
      const value = await run<boolean | undefined>('readonly', (store) =>
        store.get(fingerprintKey(pourId, hash))
      );
      return value === true;
    },
    async add(pourId, hash) {
      await run<IDBValidKey>('readwrite', (store) => store.put(true, fingerprintKey(pourId, hash)));
    },
    async remove(pourId, hash) {
      await run<undefined>('readwrite', (store) => store.delete(fingerprintKey(pourId, hash)));
    }
  };
}

/** Same swallow-your-own-failures shape as `resilientStore`, above. */
export function resilientFingerprintStore(): PhotoFingerprintStore {
  const fallback = memoryFingerprintStore();
  let primary: PhotoFingerprintStore | null = null;
  try {
    primary = typeof indexedDB === 'undefined' ? null : idbFingerprintStore();
  } catch {
    primary = null;
  }

  async function tolerate<T>(
    action: (store: PhotoFingerprintStore) => Promise<T>,
    fallbackValue: T
  ): Promise<T> {
    if (primary) {
      try {
        return await action(primary);
      } catch {
        primary = null;
      }
    }
    try {
      return await action(fallback);
    } catch {
      return fallbackValue;
    }
  }

  return {
    has: (pourId, hash) => tolerate((s) => s.has(pourId, hash), false),
    add: (pourId, hash) => tolerate((s) => s.add(pourId, hash), undefined),
    remove: (pourId, hash) => tolerate((s) => s.remove(pourId, hash), undefined)
  };
}

/* ==========================================================================
 * Transport — the three network steps, isolated so they can be faked
 * ======================================================================== */

export interface PhotoTransport {
  requestUploadUrl(
    pourId: number,
    body: {
      content_type: string;
      bytes: number;
      /** SHA-256 of the bytes below — see attempt()'s call site. */
      content_hash: string;
      section: PhotoSection | null;
      row_id: number | null;
    }
  ): Promise<PhotoUploadUrlResponse>;
  putBytes(grant: PhotoUploadUrlResponse, blob: Blob): Promise<void>;
  attach(pourId: number, body: AttachPhotoRequest): Promise<Photo>;
}

/** Carries an HTTP status so `isRetryable` can tell "try later" from "never". */
export class UploadError extends Error {
  readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'UploadError';
    this.status = status;
  }
}

const httpTransport: PhotoTransport = {
  requestUploadUrl: (pourId, body) => api.photoUploadUrl(pourId, body),

  async putBytes(grant, blob) {
    // Deliberately NOT through api.ts's `call()`: this goes to a signed URL,
    // is not a JSON route, and must not be counted as an API failure.
    const response = await fetch(grant.upload_url, {
      method: grant.method,
      headers: grant.headers,
      credentials: 'same-origin',
      body: blob
    });
    if (!response.ok) {
      throw new UploadError(`Upload rejected (${response.status}).`, response.status);
    }
  },

  async attach(pourId, body) {
    const response = await api.attachPhoto(pourId, body);
    return response.photo;
  }
};

/* ==========================================================================
 * Retry policy
 * ======================================================================== */

const RETRY_BASE_MS = 2_000;
const RETRY_MAX_MS = 120_000;

/**
 * Backoff for attempt N (1-based): 2 s, 4 s, 8 s ... capped at 2 minutes.
 *
 * Capped rather than exhausted: the queue never gives up on a retryable
 * failure, because giving up is exactly the "silently vanishing" the spec
 * forbids. A photo taken in a dead spot lands when the signal comes back.
 */
export function retryDelayMs(attempts: number): number {
  const n = Math.max(1, Math.floor(attempts));
  return Math.min(RETRY_MAX_MS, RETRY_BASE_MS * 2 ** (n - 1));
}

/**
 * Whether another attempt could plausibly succeed.
 *
 * Not retryable: the session is read-only (a client must not be able to write
 * at all), the pour is gone, or the image itself was refused. Those want the
 * user's attention, not a loop.
 */
export function isRetryable(error: unknown): boolean {
  if (isApiError(error)) {
    if (error.offline) return true;
    if (error.code === 'forbidden' || error.code === 'not_found') return false;
    if (error.code === 'validation_failed' || error.code === 'bad_request') return false;
    // 'unauthorized' IS retryable: the session expires on a 12 h shift and
    // re-authentication must not cost the photos taken since.
    return true;
  }
  if (error instanceof UploadError) {
    if (error.status === 403 || error.status === 404 || error.status === 422) return false;
    // 410 = the grant expired. Retrying mints a fresh grant, so it recovers.
    return true;
  }
  return true;
}

/* ==========================================================================
 * Injectable dependencies (the test seam)
 * ======================================================================== */

export interface PhotoQueueDeps {
  transport: PhotoTransport;
  store: PhotoBlobStore;
  /** Duplicate-fingerprint ledger — see "Fingerprinting" above. */
  fingerprints: PhotoFingerprintStore;
  /** SHA-256 of a Blob's bytes. Injectable so a test can fake a failure. */
  hash: (blob: Blob) => Promise<string>;
  now: () => number;
  previewUrl: (blob: Blob) => string;
  schedule: (fn: () => void, ms: number) => unknown;
  cancel: (handle: unknown) => void;
  uuid: () => string;
  /**
   * `indexedDB.open`, injectable so `openDb()`'s `onblocked`/`onerror`/
   * `onsuccess` wiring is directly testable with a fake request object —
   * this repo has no `fake-indexeddb`, and `typeof indexedDB === 'undefined'`
   * in the Node test environment means `resilientStore`'s own gate would
   * otherwise make `openDb()` unreachable from any test.
   */
  openIndexedDb: (name: string, version: number) => IDBOpenDBRequest;
}

function defaultUuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `photo-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

const deps: PhotoQueueDeps = {
  transport: httpTransport,
  store: resilientStore(),
  fingerprints: resilientFingerprintStore(),
  hash: sha256Hex,
  now: () => Date.now(),
  previewUrl: (blob) => {
    try {
      return URL.createObjectURL(blob);
    } catch {
      return '';
    }
  },
  schedule: (fn, ms) => setTimeout(fn, ms),
  cancel: (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
  uuid: defaultUuid,
  openIndexedDb: (name, version) => indexedDB.open(name, version)
};

/** Replace any dependency. Tests only; production never calls this. */
export function configurePhotoQueue(overrides: Partial<PhotoQueueDeps>): void {
  Object.assign(deps, overrides);
}

/** Empty the queue without touching the server. Tests only. */
export function resetPhotoQueue(): void {
  if (timer !== null) deps.cancel(timer);
  timer = null;
  running = false;
  queue.set([]);
  storageProblem.set(null);
}

/* ==========================================================================
 * Attachment notifications
 * ======================================================================== */

type AttachedHandler = (photo: Photo) => void;
const attachedHandlers = new Set<AttachedHandler>();

/**
 * Fired when a queued photo becomes a row. Screens use it to refresh their
 * photo list without polling.
 */
export function onPhotoAttached(handler: AttachedHandler): () => void {
  attachedHandlers.add(handler);
  return () => {
    attachedHandlers.delete(handler);
  };
}

function emitAttached(photo: Photo): void {
  for (const handler of attachedHandlers) {
    try {
      handler(photo);
    } catch {
      /* a listener must never break the queue */
    }
  }
}

/* ==========================================================================
 * The queue engine
 * ======================================================================== */

let running = false;
let timer: unknown = null;

function scheduleNext(): void {
  if (timer !== null) {
    deps.cancel(timer);
    timer = null;
  }
  const due = get(queue)
    .filter((item) => item.retryable)
    .map((item) => item.next_attempt_at);
  if (due.length === 0) return;
  const delay = Math.max(0, Math.min(...due) - deps.now());
  timer = deps.schedule(() => {
    timer = null;
    void flushPhotoQueue();
  }, delay);
}

async function attempt(entry: PendingPhoto): Promise<void> {
  patchEntry(entry.id, { status: 'uploading', error: null });

  const record = await deps.store.get(entry.id);
  const blob = record?.blob;
  if (!blob) {
    // The bytes are gone (storage cleared under us). Say so plainly rather
    // than retry forever against nothing.
    patchEntry(entry.id, {
      status: 'failed',
      retryable: false,
      error: 'The image is no longer on this device.'
    });
    return;
  }

  try {
    let key = entry.r2_key;

    if (!key) {
      // The server half of "Duplicate photos": the hash of the DOWNSCALED
      // bytes (not entry.fingerprint, which is the original file's hash) —
      // this is what the grant is keyed on, so it has to match the bytes the
      // PUT below actually sends. See src/api/photos.ts buildPhotoKey.
      const contentHash = await deps.hash(blob);
      const grant = await deps.transport.requestUploadUrl(entry.pour_id, {
        content_type: blob.type || 'image/jpeg',
        bytes: blob.size,
        content_hash: contentHash,
        section: entry.section,
        row_id: entry.row_id
      });
      await deps.transport.putBytes(grant, blob);
      key = grant.r2_key;
      // Remember the key BEFORE attaching: if the attach fails, the retry
      // resumes at step 3 instead of pushing the bytes over site WiFi twice.
      patchEntry(entry.id, { r2_key: key });
    }

    const photo = await deps.transport.attach(entry.pour_id, {
      r2_key: key,
      section: entry.section,
      row_id: entry.row_id,
      caption: entry.caption,
      taken_at: entry.taken_at,
      width: entry.width,
      height: entry.height,
      bytes: entry.bytes
    });

    removeEntry(entry.id, true);
    emitAttached(photo);
  } catch (cause) {
    const attempts = entry.attempts + 1;
    const retryable = isRetryable(cause);
    patchEntry(entry.id, {
      status: 'failed',
      attempts,
      retryable,
      error: cause instanceof Error ? cause.message : 'Upload failed.',
      next_attempt_at: deps.now() + retryDelayMs(attempts)
    });
  }
}

/**
 * Work through everything that is due, oldest first, one at a time.
 *
 * Serial on purpose: a phone on site WiFi uploading four photos at once is
 * slower and more failure-prone than uploading them in turn, and the hot path
 * (chute test entry) must stay responsive while it happens.
 */
export async function flushPhotoQueue(): Promise<void> {
  if (running) return;
  running = true;
  try {
    for (;;) {
      const now = deps.now();
      const next = get(queue)
        .filter((item) => item.retryable && item.next_attempt_at <= now)
        .sort((a, b) => a.next_attempt_at - b.next_attempt_at)[0];
      if (!next) break;
      await attempt(next);
    }
  } finally {
    running = false;
    scheduleNext();
  }
}

/* ==========================================================================
 * Public queue operations
 * ======================================================================== */

export interface CapturePhotoOptions {
  section?: PhotoSection | null;
  rowId?: number | null;
  caption?: string | null;
  /** Overrides the capture time. Defaults to now. */
  takenAt?: Timestamp;
}

/**
 * The client half of "Duplicate photos": hash `file`'s ORIGINAL bytes and
 * check them against every photo already added to `pourId` from this device
 * — pending or attached, whole-pour scope (see the "Fingerprinting" header
 * above for why original bytes and why permanent). Throws
 * `DuplicatePhotoError` on a match; otherwise returns the fingerprint, so
 * `capturePhoto` can hand it straight to `enqueuePhoto` without hashing
 * twice.
 *
 * Never blocks a genuine capture: if hashing itself fails (no SubtleCrypto —
 * an old WebView, a non-secure context), this resolves `null` rather than
 * throwing. A courtesy check that could refuse a photo the queue would
 * otherwise happily take is worse than no check at all.
 */
export async function guardAgainstDuplicatePhoto(pourId: number, file: Blob): Promise<string | null> {
  let hash: string;
  try {
    hash = await deps.hash(file);
  } catch {
    return null;
  }
  if (await deps.fingerprints.has(pourId, hash)) {
    throw new DuplicatePhotoError();
  }
  return hash;
}

/**
 * Put an already-downscaled image on the queue.
 *
 * Resolves once the photo is on the device and visible as pending — never
 * waits for the network. Exported separately from `capturePhoto` so callers
 * that already hold a prepared JPEG (and tests, which have no canvas) can use
 * the queue directly.
 *
 * `fingerprint` is the ORIGINAL file's hash from `guardAgainstDuplicatePhoto`
 * (or null when there is none — a direct `enqueuePhoto` call, or hashing
 * failed). When present it is recorded in the duplicate ledger before this
 * resolves, so a second `guardAgainstDuplicatePhoto` call issued right after
 * — the same file picked twice in one multi-select — reliably sees it.
 */
export async function enqueuePhoto(
  pourId: number,
  image: DownscaledPhoto,
  options: CapturePhotoOptions = {},
  fingerprint: string | null = null
): Promise<PendingPhoto> {
  const entry: PendingPhoto = {
    id: deps.uuid(),
    pour_id: pourId,
    section: options.section ?? null,
    row_id: options.rowId ?? null,
    caption: options.caption ?? null,
    taken_at: options.takenAt ?? new Date().toISOString(),
    width: image.width,
    height: image.height,
    bytes: image.bytes,
    r2_key: null,
    status: 'queued',
    attempts: 0,
    error: null,
    next_attempt_at: deps.now(),
    retryable: true,
    preview_url: deps.previewUrl(image.blob),
    fingerprint
  };

  // Persist before announcing, so a crash between the two cannot show a
  // thumbnail for a photo that is not actually stored anywhere.
  await deps.store.put({ id: entry.id, blob: image.blob, photo: strip(entry) });
  queue.update((list) => [...list, entry]);

  if (fingerprint !== null) {
    try {
      await deps.fingerprints.add(pourId, fingerprint);
    } catch {
      // Best-effort: losing the ledger entry only loses a future courtesy
      // check, never the photo itself.
    }
  }

  void flushPhotoQueue();
  return entry;
}

/**
 * Capture -> downscale -> queue. The entry point a PhotoField calls.
 *
 * Checks for a duplicate BEFORE downscaling (`guardAgainstDuplicatePhoto`) —
 * no point spending the CPU/battery on a canvas re-encode of a file this
 * device has already added. Throws `DuplicatePhotoError` when it is one;
 * PhotoField catches that specifically and tells the person, rather than
 * queueing nothing and leaving the button looking broken.
 *
 * If the downscale itself fails (an old WebView with no canvas, an image the
 * browser cannot decode) the original file is queued unchanged when it is an
 * acceptable type and within the server's cap. Uploading a large photo is a
 * far better outcome than losing the evidence.
 */
export async function capturePhoto(
  pourId: number,
  file: Blob,
  options: CapturePhotoOptions = {}
): Promise<PendingPhoto> {
  const fingerprint = await guardAgainstDuplicatePhoto(pourId, file);

  let image: DownscaledPhoto;
  try {
    image = await downscalePhoto(file);
  } catch (cause) {
    const type = (file.type || '').toLowerCase();
    const usable =
      (type === 'image/jpeg' || type === 'image/png' || type === 'image/webp') &&
      file.size > 0 &&
      file.size <= PHOTO_MAX_BYTES;
    if (!usable) throw cause;
    image = {
      blob: file,
      width: 0,
      height: 0,
      bytes: file.size,
      original_bytes: file.size,
      content_type: type
    };
  }
  return enqueuePhoto(pourId, image, options, fingerprint);
}

/** Try one pending photo again now, whatever its backoff said. */
export async function retryPhoto(id: string): Promise<void> {
  const entry = get(queue).find((item) => item.id === id);
  if (!entry) return;
  patchEntry(id, { retryable: true, next_attempt_at: deps.now(), error: null, status: 'queued' });
  await flushPhotoQueue();
}

/** Try every pending photo again now. Bound to the unsaved-work retry button. */
export async function retryAllPhotos(): Promise<void> {
  const now = deps.now();
  for (const entry of get(queue)) {
    patchEntry(entry.id, { retryable: true, next_attempt_at: now, error: null, status: 'queued' });
  }
  await flushPhotoQueue();
}

/**
 * Throw a pending photo away. The ONLY way one leaves the queue unattached,
 * and it is always an explicit user action.
 *
 * Also forgets the entry's duplicate fingerprint, if it has one: discarding
 * means this photo is no longer in the record, so it must be possible to add
 * the exact same bytes again afterwards — "already added" would otherwise be
 * a straightforward lie the moment the discard completes. Fire-and-forget,
 * same as the fingerprint write in `enqueuePhoto`; `PhotoFingerprintStore`
 * never throws (see `resilientFingerprintStore`), and even if it somehow did,
 * losing this is a lost courtesy check, never a lost photo.
 */
export function discardPhoto(id: string): void {
  const entry = get(queue).find((item) => item.id === id);
  removeEntry(id, true);
  if (entry?.fingerprint != null) {
    void deps.fingerprints.remove(entry.pour_id, entry.fingerprint);
  }
  scheduleNext();
}

/**
 * Reload the queue from the device. Call once at app start.
 *
 * Everything comes back as 'queued' with a fresh preview URL: object URLs die
 * with the document, and a status of 'uploading' from a previous session is
 * meaningless now.
 */
export async function restorePhotoQueue(): Promise<void> {
  const records = await deps.store.all();
  if (records.length === 0) return;

  const now = deps.now();
  const restored = records
    .filter((record) => record?.photo && record.blob)
    .map<PendingPhoto>((record) => ({
      ...record.photo,
      status: 'queued',
      retryable: true,
      next_attempt_at: now,
      preview_url: deps.previewUrl(record.blob)
    }));

  queue.update((list) => {
    const known = new Set(list.map((item) => item.id));
    return [...list, ...restored.filter((item) => !known.has(item.id))];
  });

  await flushPhotoQueue();
}

// Coming back into signal is the single most likely moment for a queued photo
// to succeed, so take it.
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    void retryAllPhotos();
  });
}
